import { defineWidgetCore } from "../../widgetDefinition";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import type { Table2DComponentSpec, Table2DDataSpec } from "../../Processing/types";
import { renderTable2D } from "../../PDF/renderers/table2dPdfRenderer";
import {
  buildTable2DGrid, gridColLabels, heatmapCellColors, labelLines, measuredColWidth, TEXT_DARK,
} from "../../WidgetComponents/table2dGrid";
import { formatBuilderDateRange } from "../../PDF/snapshotDateRange";
export const table2dCore = defineWidgetCore<Table2DComponentSpec>({
  process(spec, response) {
    const nonSeries = response?.non_series_response;
    const table2d = nonSeries?.type === "table_2d" ? nonSeries.data : null;
    return {
      type: "table_2d",
      spec,
      table2d: table2d ?? null,
    };
  },
  pdf: {
    buildSnapshot({ processed, slicerValues }) {
      if (processed.type !== "table_2d") return null;
      const spec = processed.spec;
      const title = resolveTemplate(spec.display.title ?? "", slicerValues).resolved || null;

      // Grey date-range + subject subtitle, matching the generic table widget.
      const misc = spec.table_2d_builder_misc;
      const datePart = formatBuilderDateRange(misc as Parameters<typeof formatBuilderDateRange>[0]);
      const names = misc?.selectedSubjectNames ?? [];
      const MAX = 10;
      const subjectPart =
        names.length === 0
          ? "Full Team"
          : names.length <= MAX
            ? names.join(", ")
            : `${names.slice(0, MAX).join(", ")} (+${names.length - MAX} more)`;
      const subtitle = datePart ? `${datePart} | ${subjectPart}` : subjectPart;

      const table2d = (processed as any).table2d as Table2DDataSpec | null;
      if (!table2d) return { type: "table_2d", title, subtitle, colorize: spec.colorize };

      const grid = buildTable2DGrid(table2d, spec);

      if (spec.colorize) {
        // Heatmap: precompute per-cell background + text color so the PDF
        // renderer matches the live component exactly.
        const heatmap = {
          compact: !spec.showDataLabels,
          cols: grid.cols.map((c, ci) => ({
            label: c.label, pinned: c.pinned, width: measuredColWidth(c.label, false, gridColLabels(grid, ci)),
          })),
          rows: grid.rows.map((r) => ({
            label: r.label,
            pinned: r.pinned,
            cells: r.cells.map((cell, ci) => {
              const isPinnedCell = r.pinned || grid.cols[ci].pinned;
              const { bg, color } = heatmapCellColors(cell.value, cell.hex, isPinnedCell);
              return {
                label: spec.showDataLabels && cell.value != null ? cell.label : "",
                bg,
                color,
              };
            }),
          })),
        };
        return { type: "table_2d", title, subtitle, colorize: true, heatmap };
      }

      // Plain table: same grid, rendered as a bordered table (white cells, colored
      // value text) — shares the heatmap renderer's column-fit/truncation logic.
      // A text answer label wraps once its column hits the cap, so the rows grow
      // to the tallest label, the same as the live antd table.
      const plainWidths = grid.cols.map((c, ci) => measuredColWidth(c.label, true, gridColLabels(grid, ci)));
      const rowLines = Math.max(1, ...grid.cols.map((_, ci) => labelLines(gridColLabels(grid, ci), plainWidths[ci])));
      const plainGrid = {
        compact: false,
        plain: true,
        rowLines,
        cols: grid.cols.map((c, ci) => ({ label: c.label, pinned: c.pinned, width: plainWidths[ci] })),
        rows: grid.rows.map((r) => ({
          label: r.label,
          pinned: r.pinned,
          cells: r.cells.map((cell, ci) => {
            const isPinned = r.pinned || grid.cols[ci].pinned;
            const color = cell.value == null ? "#bfbfbf" : isPinned ? TEXT_DARK : (cell.hex ?? TEXT_DARK);
            // Colored value cells render bold, matching the regular table.
            return { label: cell.label, bg: "#ffffff", color, bold: !!cell.hex };
          }),
        })),
      };

      return { type: "table_2d", title, subtitle, colorize: false, heatmap: plainGrid };
    },
    render({ snapshot, rect, scale }) {
      if (snapshot.type !== "table_2d") return null;
      return renderTable2D(snapshot, rect, scale);
    },
  },
});
